import { amber, red, grey, purple } from '@mui/material/colors';
import {
  createTheme,
  ThemeProvider as MThemeProvider,
  PaletteOptions,
  ThemeOptions,
} from '@mui/material/styles';
import React from 'react';
import { useDarkMode } from './DarkModeProvider';

declare module '@mui/material/styles' {
  interface Palette {
    purple: {
      main: string;
    };
  }
  interface PaletteOptions {
    purple: {
      main: string;
    };
  }
}

export interface CustomPaletteOptions extends PaletteOptions {
  primary_darker: {
    main: string;
    secondary: string;
  };
  purple: {
    main: string;
  };
}
interface CustomThemeOptions extends ThemeOptions {
  palette: CustomPaletteOptions;
}

// Light theme
const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1083c6',
    },
    secondary: {
      main: '#00b6a5',
    },
    primary_darker: {
      main: '#08527e',
      secondary: '#0d78b7',
    },
    purple: {
      main: '#A743EE',
    },
    error: {
      main: red.A200,
    },
    warning: {
      main: amber.A700,
    },
    twitter: {
      main: '#0F7BBA',
    },
    light: {
      primary: 'rgba(16, 131, 198, 0.12)',
      secondary: 'rgba(0, 182, 165, 0.12)',
      error: 'rgba(255, 82, 82, 0.12)',
      warning: 'rgba(255, 171, 0, 0.12)',
      default: grey[200] as string,
      twitter: 'rgba(0, 172, 238, 0.12)',
    },
    background: {
      default: '#ebedef',
      paper: '#ffffff',
    },
    text: {
      primary: '#000000',
      secondary: '#666666',
    },
    contrastThreshold: 3,
    tonalOffset: 0.2,
  },
  typography: {
    fontFamily: [
      'Maven Pro',
      '-apple-system',
      'system-ui',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontSize: 32,
    },
    h2: {
      fontSize: 28,
    },
    h3: {
      fontSize: 24,
    },
    h4: {
      fontSize: 18,
    },
    h5: {
      fontSize: 16,
      lineHeight: 1.2,
      fontWeight: 500,
    },
    h6: {
      fontSize: 15,
      lineHeight: 1.2,
      fontWeight: 500,
    },
    body1: {
      fontSize: 14,
      lineHeight: 1.2,
    },
    body2: {
      fontSize: 13,
      lineHeight: 1.3,
    },
  },
  components: {
    MuiChip: {
      styleOverrides: {
        colorPrimary: {
          fontWeight: '500 !important',
          color: '#1083c6 !important',
          backgroundColor: 'rgba(16, 131, 198, 0.12) !important',
        },
        clickableColorPrimary: {
          fontWeight: '500 !important',
          color: '#1083c6',
          backgroundColor: 'rgba(16, 131, 198, 0.12) !important',
          '&:focus': {
            backgroundColor: 'rgba(16, 131, 198, 0.12) !important',
          },
          '&:hover': {
            backgroundColor: 'rgba(16, 131, 198, 0.12) !important',
          },
        },
      }
    },

    MuiTooltip: {
      styleOverrides: {
        arrow: {
          color: 'rgba(16, 131, 198, 0.92)',
        },
        tooltip: {
          borderRadius: 16,
          fontWeight: 'normal',
          padding: 12,
          fontSize: 16,
          backgroundColor: 'rgba(16, 131, 198, 0.92)',
        },
      }
    },
    MuiSvgIcon: {
      styleOverrides: {
        colorAction: {
          color: amber.A700,
        },
      }
    },
  },
} as CustomThemeOptions);

// Dark theme
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#2d5a87',
    },
    secondary: {
      main: '#00d4aa',
    },
    primary_darker: {
      main: '#1e3a52',
      secondary: '#2d5a87',
    },
    purple: {
      main: '#ba68c8',
    },
    error: {
      main: '#f44336',
    },
    warning: {
      main: '#ff9800',
    },
    twitter: {
      main: '#29b6f6',
    },
    light: {
      primary: 'rgba(45, 90, 135, 0.12)',
      secondary: 'rgba(0, 212, 170, 0.12)',
      error: 'rgba(244, 67, 54, 0.12)',
      warning: 'rgba(255, 152, 0, 0.12)',
      default: '#1e1e1e',
      twitter: 'rgba(41, 182, 246, 0.12)',
    },
    background: {
      default: '#1e1e1e',
      paper: '#1e1e1e',
    },
    text: {
      primary: '#ffffff',
      secondary: '#b0b0b0',
    },
    contrastThreshold: 3,
    tonalOffset: 0.2,
  },
  typography: {
    fontFamily: [
      'Maven Pro',
      '-apple-system',
      'system-ui',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontSize: 32,
    },
    h2: {
      fontSize: 28,
    },
    h3: {
      fontSize: 24,
    },
    h4: {
      fontSize: 18,
    },
    h5: {
      fontSize: 16,
      lineHeight: 1.2,
      fontWeight: 500,
    },
    h6: {
      fontSize: 15,
      lineHeight: 1.2,
      fontWeight: 500,
    },
    body1: {
      fontSize: 14,
      lineHeight: 1.2,
    },
    body2: {
      fontSize: 13,
      lineHeight: 1.3,
    },
  },
  components: {

    MuiTooltip: {
      styleOverrides: {
        arrow: {
          color: '#2d5a87 !important',
          '&::before': {
            backgroundColor: '#2d5a87 !important',
          },
        },
        tooltip: {
          borderRadius: 16,
          fontWeight: 'normal',
          padding: 12,
          fontSize: 16,
          backgroundColor: '#2d5a87 !important',
          color: '#ffffff !important',
        },
      }
    },
    MuiSvgIcon: {
      styleOverrides: {
        colorAction: {
          color: '#ff9800',
        },
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: '#1e1e1e',
          // Preserve colored bars for summary cards
          '&[class*="summaryContainer"]': {
            backgroundColor: '#1e1e1e',
            color: '#ffffff',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
            border: '1px solid #404040',
            '& .MuiBox-root[class*="line"]': {
              // Don't override the background color
            },
            '& .MuiBox-root[class*="pepLine"]': {
              // Don't override the background color
            },
            '& .MuiBox-root[class*="eventLine"]': {
              // Don't override the background color
            },
          },
        },
      },
    },
    MuiBox: {
      styleOverrides: {
        root: {
          // Preserve colored bars for summary cards
          '&[class*="line"]': {
            // Don't override the background color - let it be set by the component
          },
          '&[class*="pepLine"]': {
            // Don't override the background color - let it be set by the component
          },
          '&[class*="eventLine"]': {
            // Don't override the background color - let it be set by the component
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#1e1e1e',
          color: '#ffffff',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
          borderBottom: '1px solid #333333',
          '& .MuiToolbar-root': {
            backgroundColor: '#1e1e1e',
            color: '#ffffff',
          },
          '& .MuiInputBase-root': {
            backgroundColor: '#1e1e1e',
            color: '#ffffff',
            '& fieldset': {
              borderColor: '#404040',
            },
            '&:hover fieldset': {
              borderColor: '#555555',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#2d5a87',
            },
          },
          '& .MuiInputLabel-root': {
            color: '#b0b0b0',
          },
          '& .MuiInputBase-input': {
            color: '#ffffff',
            '&::placeholder': {
              color: '#b0b0b0',
            },
          },
          '& .MuiIconButton-root': {
            color: '#ffffff',
            '&:hover': {
              backgroundColor: '#1e1e1e',
            },
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1e1e1e',
          color: '#ffffff',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: '#1e1e1e',
          boxShadow: 'none',
          '& .MuiTableCell-head': {
            color: '#ffffff',
            borderBottomColor: '#404040',
            boxShadow: 'none',
            textShadow: 'none',
            '& .MuiTypography-root': {
              textShadow: 'none',
              boxShadow: 'none',
            },
            '&:hover': {
              backgroundColor: 'transparent',
            },
          },
        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-body': {
            color: '#ffffff',
            borderBottomColor: '#404040',
          },
          '& .MuiTableRow-root:hover': {
            backgroundColor: 'transparent',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          backgroundColor: '#1e1e1e',
          color: '#ffffff',
          borderBottomColor: '#404040',
        },
        head: {
          backgroundColor: '#1e1e1e',
          color: '#ffffff',
          borderBottomColor: '#404040',
          boxShadow: 'none',
          textShadow: 'none',
          '& .MuiTypography-root': {
            textShadow: 'none',
            boxShadow: 'none',
          },
          '&:hover': {
            backgroundColor: 'transparent',
          },
        },
        body: {
          backgroundColor: '#1e1e1e',
          color: '#ffffff',
          borderBottomColor: '#404040',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          backgroundColor: '#1e1e1e',
          '&:hover': {
            backgroundColor: 'transparent',
          },
          '&:nth-of-type(even)': {
            backgroundColor: '#1e1e1e',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'uppercase',
        },
        contained: {
          backgroundColor: '#1e1e1e',
          color: '#ffffff',
          '&:hover': {
            backgroundColor: '#1a1a1a',
          },
        },
        outlined: {
          borderColor: '#404040',
          color: '#ffffff',
          '&:hover': {
            borderColor: '#555555',
            backgroundColor: '#1e1e1e',
          },
        },
        text: {
          color: '#ffffff',
          '&:hover': {
            backgroundColor: '#1e1e1e',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: '#1e1e1e',
            '& fieldset': {
              borderColor: '#404040',
            },
            '&:hover fieldset': {
              borderColor: '#555555',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#2d5a87',
            },
          },
          '& .MuiInputLabel-root': {
            color: '#b0b0b0',
          },
          '& .MuiInputBase-input': {
            color: '#ffffff',
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          '& .MuiSwitch-track': {
            backgroundColor: '#404040',
          },
          '& .MuiSwitch-thumb': {
            backgroundColor: '#ffffff',
          },
          '&.Mui-checked .MuiSwitch-track': {
            backgroundColor: '#2d5a87',
          },
          '&.Mui-checked .MuiSwitch-thumb': {
            backgroundColor: '#ffffff',
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          '&:hover': {
            backgroundColor: '#1e1e1e',
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1e1e1e',
          color: '#ffffff',
          border: '1px solid #404040',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          '&:hover': {
            backgroundColor: '#1e1e1e',
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1e1e1e',
          color: '#ffffff',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          color: '#ffffff',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          color: '#ffffff',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: '#1e1e1e',
          color: '#ffffff',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          color: '#ffffff',
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: '#1e1e1e',
            '& fieldset': {
              borderColor: '#404040',
            },
            '&:hover fieldset': {
              borderColor: '#555555',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#2d5a87',
            },
          },
          '& .MuiInputLabel-root': {
            color: '#b0b0b0',
          },
          '& .MuiInputBase-input': {
            color: '#ffffff',
            '&::placeholder': {
              color: '#b0b0b0',
            },
          },
        },
        paper: {
          backgroundColor: '#1e1e1e',
          color: '#ffffff',
          border: '1px solid #404040',
        },
        listbox: {
          backgroundColor: '#1e1e1e',
          color: '#ffffff',
          '& .MuiAutocomplete-option': {
            '&:hover': {
              backgroundColor: '#1e1e1e',
            },
            '&[aria-selected="true"]': {
              backgroundColor: '#1e1e1e',
            },
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          backgroundColor: '#1e1e1e',
          color: '#ffffff',
          '& fieldset': {
            borderColor: '#404040',
          },
          '&:hover fieldset': {
            borderColor: '#555555',
          },
          '&.Mui-focused fieldset': {
            borderColor: '#2d5a87',
          },
          '& input': {
            color: '#ffffff',
            '&:placeholder': {
              color: '#b0b0b0',
            },
          },
        },
      },
    },
  },
} as CustomThemeOptions);

const ThemeProvider: React.FC<IProps> = ({ children }) => {
  const { isDarkMode } = useDarkMode();
  const theme = isDarkMode ? darkTheme : lightTheme;

  return <MThemeProvider theme={theme}>{children}</MThemeProvider>;
};

interface IProps {
  children: React.ReactNode;
}

export default ThemeProvider;
